import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  // FOR DL APP COMMENTOUT TEMP..
  // FaCalendarCheck,
  // FaChalkboardTeacher,
  FaCheckCircle,
  // FOR DL APP COMMENTOUT TEMP..
  // FaComments,
  // FaDumbbell,
  FaEdit,
  FaEnvelope,
  // FOR DL APP COMMENTOUT TEMP..
  // FaGift,
  // FaHeartbeat,
  FaHome,
  FaIdCard,
  FaCamera,
  // FOR DL APP COMMENTOUT TEMP..
  // FaMobileAlt,
  FaPhoneAlt,
  FaSave,
  FaShieldAlt,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import LoginPopup from "../../components/LogIn/LoginPopup";
// FOR DL APP COMMENTOUT TEMP..
// import { getAppDownloadUrl, PLAY_STORE_URL } from "../../utils/appDownload";
import "../../styles/Profile.css";

const emptyForm = {
  full_name: "",
  phone_number: "",
};

const formatDate = (value, fallback = "Not available") => {
  if (!value) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getInitials = (name, email) => {
  const source = name || email || "Victoria Sports";
  const initials = source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || "VS";
};

const getAvatarStorageKey = (userId) => `vsc-profile-avatar:${userId}`;

const readStoredAvatar = (userId) => {
  if (!userId) return "";

  try {
    return localStorage.getItem(getAvatarStorageKey(userId)) || "";
  } catch (error) {
    return "";
  }
};

// FOR DL APP COMMENTOUT TEMP..
// const appFeatures = [
//   {
//     icon: FaChalkboardTeacher,
//     title: "Classes and Coaches",
//     text: "Enroll in classes and choose the coach that fits your training style.",
//   },
//   {
//     icon: FaCalendarCheck,
//     title: "Rentals and Requests",
//     text: "Request memberships, sports facilities, and event spaces from your phone.",
//   },
//   {
//     icon: FaComments,
//     title: "Coach Chat",
//     text: "Message your coach faster when you need updates or guidance.",
//   },
//   {
//     icon: FaHeartbeat,
//     title: "Google Fit Tracking",
//     text: "Track health and fitness status through Google Fit integration.",
//   },
//   {
//     icon: FaGift,
//     title: "Rewards Points",
//     text: "Earn points when you enroll or avail services, then redeem them for sessions, food, and more.",
//   },
// ];

const resizeAvatarImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => {
    const image = new Image();

    image.onload = () => {
      const size = 512;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = size;
      canvas.height = size;

      const scale = Math.max(size / image.width, size / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      const x = (size - width) / 2;
      const y = (size - height) / 2;

      context.drawImage(image, x, y, width, height);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Unable to prepare selected image."));
          return;
        }

        resolve({
          blob,
          dataUrl: canvas.toDataURL("image/jpeg", 0.86),
        });
      }, "image/jpeg", 0.86);
    };

    image.onerror = () => reject(new Error("Unable to read selected image."));
    image.src = reader.result;
  };

  reader.onerror = () => reject(new Error("Unable to read selected file."));
  reader.readAsDataURL(file);
});

function Profile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showLogin, setShowLogin] = useState(false);
  const [initialSignup, setInitialSignup] = useState(false);
  const [initialReset, setInitialReset] = useState(false);
  const avatarInputRef = useRef(null);

  const buildFallbackProfile = useCallback((currentUser) => {
    if (!currentUser) return null;

    const metadata = currentUser.user_metadata || {};

    return {
      full_name: metadata.full_name || metadata.name || "",
      phone_number: metadata.phone || "",
      email: currentUser.email || "",
      avatar_url: metadata.avatar_url || readStoredAvatar(currentUser.id),
      created_at: currentUser.created_at,
      updated_at: currentUser.updated_at,
    };
  }, []);

  const applyProfile = useCallback((nextProfile) => {
    setProfile(nextProfile);
    setForm({
      full_name: nextProfile?.full_name || "",
      phone_number: nextProfile?.phone_number || "",
    });
  }, []);

  const hydrateProfile = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      setForm(emptyForm);
      return;
    }

    const fallbackProfile = buildFallbackProfile(currentUser);
    applyProfile(fallbackProfile);

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, phone_number, email, created_at, updated_at")
        .eq("id", currentUser.id)
        .maybeSingle();

      if (error) throw error;

      const nextProfile = {
        ...fallbackProfile,
        ...(data || {}),
        email: data?.email || fallbackProfile.email,
        created_at: data?.created_at || fallbackProfile.created_at,
        updated_at: data?.updated_at || fallbackProfile.updated_at,
      };

      applyProfile(nextProfile);
    } catch (error) {
      setMessage({
        text: "Some profile details are using your account information for now.",
        type: "info",
      });
    }
  }, [applyProfile, buildFallbackProfile]);

  useEffect(() => {
    let mounted = true;

    const initializeProfile = async () => {
      setLoading(true);
      let currentUser = null;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          applyProfile(buildFallbackProfile(currentUser));
        } else {
          setProfile(null);
          setForm(emptyForm);
        }
      } catch (error) {
        if (mounted) {
          setMessage({
            text: "Unable to load your account session. Please refresh and try again.",
            type: "error",
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }

      if (mounted && currentUser) {
        hydrateProfile(currentUser);
      }
    };

    initializeProfile();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        applyProfile(buildFallbackProfile(currentUser));
        setTimeout(() => {
          if (mounted) hydrateProfile(currentUser);
        }, 0);
      } else {
        setProfile(null);
        setForm(emptyForm);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applyProfile, buildFallbackProfile, hydrateProfile]);

  const displayName =
    profile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    "Club Member";

  const email = profile?.email || user?.email || "No email added";
  const phone = profile?.phone_number || user?.user_metadata?.phone || "Not added";
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || readStoredAvatar(user?.id);
  const initials = useMemo(
    () => getInitials(displayName, email),
    [displayName, email]
  );
  const memberSince = formatDate(profile?.created_at || user?.created_at, "New account");
  const lastSignIn = formatDate(user?.last_sign_in_at, "Not available");

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setMessage({ text: "", type: "" });
    setForm({
      full_name: profile?.full_name || "",
      phone_number: profile?.phone_number || "",
    });
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!user) return;

    const fullName = form.full_name.trim();
    const phoneNumber = form.phone_number.trim();

    if (!fullName) {
      setMessage({ text: "Full name is required.", type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const { error: profileError } = await supabase.from("profiles").upsert(
        [
          {
            id: user.id,
            full_name: fullName,
            phone_number: phoneNumber,
            email: user.email,
          },
        ],
        { onConflict: "id" }
      );

      if (profileError) throw profileError;

      const { data: authData, error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          phone: phoneNumber,
        },
      });

      if (authError) throw authError;

      const updatedUser = authData?.user || user;
      setUser(updatedUser);
      await hydrateProfile(updatedUser);
      setIsEditing(false);
      setMessage({ text: "Profile updated successfully.", type: "success" });
    } catch (error) {
      setMessage({
        text: error.message || "Unable to update profile. Please try again.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ text: "Please choose a valid image file.", type: "error" });
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: "Please choose an image smaller than 6MB.", type: "error" });
      event.target.value = "";
      return;
    }

    setAvatarSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const { blob, dataUrl } = await resizeAvatarImage(file);
      let nextAvatarUrl = dataUrl;
      let uploadedToStorage = false;

      try {
        const filePath = `${user.id}/profile.jpg`;
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(filePath, blob, {
            cacheControl: "3600",
            contentType: "image/jpeg",
            upsert: true,
          });

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("avatars").getPublicUrl(filePath);

        nextAvatarUrl = publicUrl ? `${publicUrl}?v=${Date.now()}` : dataUrl;
        uploadedToStorage = Boolean(publicUrl);
      } catch (uploadError) {
        nextAvatarUrl = dataUrl;
      }

      try {
        localStorage.setItem(getAvatarStorageKey(user.id), nextAvatarUrl);
      } catch (storageError) {
        // Local storage can be unavailable in private browsing; the preview still updates.
      }

      const nextMetadata = {
        ...(user.user_metadata || {}),
        avatar_url: nextAvatarUrl,
      };

      let updatedUser = user;
      if (uploadedToStorage) {
        const { data: authData, error: authError } = await supabase.auth.updateUser({
          data: nextMetadata,
        });

        if (authError) throw authError;
        updatedUser = authData?.user || user;
        setUser(updatedUser);
      }

      setProfile({
        ...(profile || buildFallbackProfile(updatedUser)),
        avatar_url: nextAvatarUrl,
      });

      setMessage({
        text: uploadedToStorage
          ? "Profile picture updated successfully."
          : "Profile picture updated on this browser.",
        type: "success",
      });
    } catch (error) {
      setMessage({
        text: error.message || "Unable to update profile picture. Please try another image.",
        type: "error",
      });
    } finally {
      setAvatarSaving(false);
      event.target.value = "";
    }
  };

  const openLogin = (signupMode = false) => {
    setInitialSignup(signupMode);
    setShowLogin(true);
  };

  const openChangePassword = () => {
  setInitialSignup(false);
  setInitialReset(true);
  setShowLogin(true);
  };

  // FOR DL APP COMMENTOUT TEMP..
  // const handleAppDownload = (event) => {
  //   event.preventDefault();
  //   window.open(getAppDownloadUrl(), "_blank", "noopener,noreferrer");
  // };

  const heroBackground = {
    backgroundImage:
      'linear-gradient(90deg, rgba(3, 20, 13, 0.78), rgba(18, 91, 59, 0.42), rgba(0, 0, 0, 0.18)), url("/vscimages/hero/memHeroB.jpg")',
  };

  return (
    <main className="profile-page">
      <section className="profile-hero" style={heroBackground}>
        <div className="profile-hero-content">
          <span className="profile-kicker">Victoria Sports Club</span>
          <h1>{user ? "My Profile" : "Account Access"}</h1>
          <p>{user ? `Welcome back, ${displayName}.` : "Sign in to view your club profile."}</p>
        </div>
      </section>

      {loading ? (
        <section className="profile-shell">
          <div className="profile-loading-card">
            <div className="profile-loader" />
            <p>Loading profile...</p>
          </div>
        </section>
      ) : user ? (
        <section className="profile-shell">
          <div className="profile-overview-card">
            <div className="profile-identity">
              <div className="profile-avatar-editor">
                <div className={avatarUrl ? "profile-avatar-large has-image" : "profile-avatar-large"}>
                  {avatarUrl ? <img src={avatarUrl} alt={`${displayName} profile`} /> : initials}
                </div>
                <button
                  type="button"
                  className="profile-avatar-edit-btn"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarSaving}
                  aria-label="Change profile picture"
                  title="Change profile picture"
                >
                  <FaCamera />
                </button> 
                <input
                  ref={avatarInputRef}
                  className="profile-avatar-input"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarSelect}
                />
              </div>
              <div>
                <span className="profile-status-badge">
                  <FaCheckCircle /> Active Account
                </span>
                <h2>{displayName}</h2>
                <p>{avatarSaving ? "Updating profile picture..." : email}</p>
              </div>
            </div>

            <div className="profile-meta-grid">
              <div className="profile-meta-item">
                <FaEnvelope />
                <span>Email</span>
                <strong>{email}</strong>
              </div>
              <div className="profile-meta-item">
                <FaPhoneAlt />
                <span>Phone</span>
                <strong>{phone}</strong>
              </div>
              <div className="profile-meta-item">
                <FaCalendarAlt />
                <span>Member Since</span>
                <strong>{memberSince}</strong>
              </div>
            </div>

            <div className="profile-action-buttons">
              <button
                type="button"
                className="profile-outline-btn"
                onClick={isEditing ? handleCancelEdit : () => setIsEditing(true)}
              >
                {isEditing ? <FaTimes /> : <FaEdit />}
                {isEditing ? "Cancel" : "Edit Profile"}
              </button>

              <button
                type="button"
                className="profile-outline-btn"
                onClick={openChangePassword}
              >
                Change Password
              </button>
            </div>
          </div>

          {message.text && (
            <div className={`profile-message ${message.type}`}>{message.text}</div>
          )}

          <div className="profile-grid">
            <form className="profile-panel profile-form-panel" onSubmit={handleSaveProfile}>
              <div className="profile-section-heading">
                <span>Personal Details</span>
                <p>Primary account information</p>
              </div>

              <label className="profile-field">
                <span>Full Name</span>
                <input
                  type="text"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleInputChange}
                  disabled={!isEditing || saving}
                  placeholder="Full name"
                />
              </label>

              <label className="profile-field">
                <span>Email Address</span>
                <input type="email" value={email} disabled />
              </label>

              <label className="profile-field">
                <span>Phone Number</span>
                <input
                  type="tel"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleInputChange}
                  disabled={!isEditing || saving}
                  placeholder="Add phone number"
                />
              </label>

              {isEditing && (
                <button type="submit" className="profile-primary-btn" disabled={saving}>
                  <FaSave />
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              )}
            </form>

            <aside className="profile-panel profile-side-panel">
              <div className="profile-section-heading">
                <span>Club Snapshot</span>
                <p>Account overview</p>
              </div>

              <div className="profile-snapshot-list">
                <div className="profile-snapshot-item">
                  <FaShieldAlt />
                  <div>
                    <strong>Account Status</strong>
                    <span>{user.email_confirmed_at ? "Verified" : "Email verification pending"}</span>
                  </div>
                </div>

                <div className="profile-snapshot-item">
                  <FaIdCard />
                  <div>
                    <strong>Profile Type</strong>
                    <span>Online Member Account</span>
                  </div>
                </div>

                <div className="profile-snapshot-item">
                  <FaCalendarAlt />
                  <div>
                    <strong>Last Sign In</strong>
                    <span>{lastSignIn}</span>
                  </div>
                </div>
              </div>

              <Link to="/#home-membership" className="profile-membership-link">
                View Membership
              </Link>
            </aside>
          </div>
        </section>
      ) : (
        <section className="profile-shell">
          <div className="profile-auth-card">
            <FaUserCircle />
            <h2>Sign in to continue</h2>
            <p>Your Victoria Sports Club profile is available after login.</p>
            <div className="profile-auth-actions">
              <button type="button" className="profile-primary-btn" onClick={() => openLogin(false)}>
                Sign In
              </button>
              <button type="button" className="profile-outline-btn" onClick={() => openLogin(true)}>
                Create Account
              </button>
              <Link to="/" className="profile-home-link">
                <FaHome /> Back to Home
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FOR DL APP COMMENTOUT TEMP.. */}
      {/* {!loading && (
        <section className="profile-shell profile-app-shell">
          <div className="profile-app-band">
            <div className="profile-app-copy">
              <span className="profile-app-eyebrow">
                <FaMobileAlt />
                Victoria Sports App
              </span>
              <h2>Manage more of your club life from your phone.</h2>
              <p>
                The Victoria Sports App helps members book classes, connect with coaches,
                request facilities, track progress, and earn rewards in one place.
              </p>
              <a
                href={PLAY_STORE_URL}
                className="profile-app-cta"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppDownload}
                aria-label="Download the Victoria Sports Club app"
              >
                <FaMobileAlt />
                Get the App
              </a>
            </div>

            <div className="profile-app-preview" aria-label="Victoria Sports App feature highlights">
              <div className="profile-phone-preview">
                <div className="profile-phone-speaker" />
                <div className="profile-phone-header">
                  <span>VSC App</span>
                  <FaDumbbell />
                </div>
                <div className="profile-phone-stat">
                  <strong>Rewards</strong>
                  <span>Points increase every enrollment</span>
                </div>
                <ul className="profile-app-feature-list">
                  {appFeatures.map(({ icon: FeatureIcon, title, text }) => (
                    <li key={title}>
                      <span className="profile-app-feature-icon">
                        <FeatureIcon />
                      </span>
                      <div>
                        <strong>{title}</strong>
                        <p>{text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )} */}

      {showLogin && (
        <LoginPopup
          initialSignup={initialSignup}
          initialReset={initialReset}
          initialEmail={user?.email || ""}
          onClose={() => {
            setShowLogin(false);
            setInitialSignup(false);
            setInitialReset(false);
          }}
        />
      )}
    </main>
  );
}

export default Profile;
